import { ref } from 'vue';
import { useLocale } from './useLocale';

import { get } from '@/api/httpClient';
import type { Building } from '@/typings/buildings/Building';
import type { GetBuildingsRequest } from '@/typings/requests/GetBuildingsRequest';

export function useBuildingsApi() {
  // GET refs
  const getBuildingByIdError = ref<string | undefined>();
  const getBuildingByIdLoading = ref(false);
  const getBuildingsByProjectIdError = ref<string | undefined>();
  const getBuildingsByProjectIdLoading = ref(false);
  const { getLocale } = useLocale();

  // GET methods
  async function getBuildingById(id: number): Promise<Building> {
    getBuildingByIdLoading.value = true;
    try {
      const lang = getLocale();
      return await get<undefined, Building>(`/buildings/${id}?locale=${lang}`);
    } catch (err) {
      getBuildingByIdError.value = err instanceof Error ? err.message : 'Unknown error';
      throw err;
    } finally {
      getBuildingByIdLoading.value = false;
    }
  }

  async function getBuildingsByProjectId(projectId: number, request?: GetBuildingsRequest): Promise<Building[]> {
    getBuildingsByProjectIdLoading.value = true;
    try {
      const queryParams = new URLSearchParams();
      const lang = getLocale();

      if (request) {
        if (request.listingType !== undefined) {
          queryParams.append('listingType', request.listingType.toString());
        }
        if (request.availabilityDates && request.availabilityDates.length > 0) {
          // Ajouter chaque date comme un paramètre séparé
          request.availabilityDates.forEach(date => {
            queryParams.append('availabilityDates', date);
          });
        }
        if (request.unitConfigurations && request.unitConfigurations.length > 0) {
          // Ajouter chaque date comme un paramètre séparé
          request.unitConfigurations.forEach(config => {
            queryParams.append('unitConfigurations', config.toString());
          });
        }
        if (request.dogAllowed != null) {
          queryParams.append('dogAllowed', request.dogAllowed.toString());
        }
        if (request.floorNumbers != null && request.floorNumbers.length > 0) {
          request.floorNumbers.forEach(floor => {
            queryParams.append('floorNumbers', floor.toString());
          });
        }
        if (request.minPrice != null) {
          queryParams.append('minPrice', request.minPrice.toString());
        }
        if (request.maxPrice != null) {
          queryParams.append('maxPrice', request.maxPrice.toString());
        }
      }
      // Always include locale
      queryParams.append('locale', lang);

      const url = `/buildings/project/${projectId}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      
      return await get<undefined, Building[]>(url);
    } catch (err) {
      getBuildingsByProjectIdError.value = err instanceof Error ? err.message : 'Unknown error';
      console.error('Failed to load buildings:', getBuildingsByProjectIdError.value);
      return [];
    } finally {
      getBuildingsByProjectIdLoading.value = false;
    }
  }

  return {
    // GET returns
    getBuildingById,
    getBuildingByIdError,
    getBuildingByIdLoading,
    getBuildingsByProjectId,
    getBuildingsByProjectIdError,
    getBuildingsByProjectIdLoading
  };
}
